import { useState, useCallback } from 'react';

export enum CalendarType {
  GLOBAL = 'GLOBAL',
  KOREA = 'KOREA',
}

interface CalendarEvent {
  id: string;
  summary: string;
  location: string;
  description: string;
  start: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
  end: {
    dateTime?: string;
    date?: string;
    timeZone?: string;
  };
}

interface GetEventsOptions {
  query?: string;
  timeMin: string;
  timeMax: string;
}

interface CreateEventPayload {
  type: CalendarType;
  summary: string;
  location: string;
  description: string;
  start: string; // ISO string or YYYY-MM-DD
  end: string; // ISO string or YYYY-MM-DD
  timeZone?: string; // Required if start/end are dateTime
}

interface UpdateEventPayload extends CreateEventPayload {
  eventId: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL;

export const useGoogleCalendar = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Calendar 이벤트 조회
   */
  const getCalendarEvents = useCallback(
    async (
      type: CalendarType,
      options: GetEventsOptions
    ): Promise<CalendarEvent[]> => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          type,
          timeMin: options.timeMin,
          timeMax: options.timeMax,
          ...(options.query && { query: options.query }),
        });

        const response = await fetch(
          `${API_BASE_URL}/google-calendar/events?${params.toString()}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error getting calendar events:', errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Calendar 이벤트 생성
   */
  const createCalendarEvent = useCallback(
    async (payload: CreateEventPayload): Promise<CalendarEvent | null> => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/google-calendar/events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // 백엔드 응답 구조에 맞게 수정 (201은 Created 상태코드)
        if ((data.statusCode === 200 || data.statusCode === 201) && data.data) {
          return data.data;
        }
        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error creating calendar event:', errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Calendar 이벤트 수정
   */
  const updateCalendarEvent = useCallback(
    async (payload: UpdateEventPayload): Promise<CalendarEvent | null> => {
      try {
        setLoading(true);
        setError(null);

        const { eventId, ...updatePayload } = payload;
        const response = await fetch(
          `${API_BASE_URL}/google-calendar/events/${eventId}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updatePayload),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error updating calendar event:', errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Calendar 이벤트 삭제
   */
  const deleteCalendarEvent = useCallback(
    async (type: CalendarType, eventId: string): Promise<boolean> => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${API_BASE_URL}/google-calendar/events/${eventId}?type=${type}`,
          {
            method: 'DELETE',
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        console.error('Error deleting calendar event:', errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    loading,
    error,
    getCalendarEvents,
    createCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
  };
};
